// ABOUTME: React Query mutation hooks for creating, updating, and deleting order intake lead time configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  OrderIntakeLeadTimeApiResponse,
  OrderIntakeLeadTimeFormValues,
  PutOrderIntakeLeadTimeRequest,
  OrderIntakeLeadTimeOverrides,
  fromDayIdentifier,
  toDayIdentifier,
} from '../types/orderIntakeLeadTime'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

const toApiDayCode = (day: string): string => {
  const identifier = toDayIdentifier(day)
  return identifier ? fromDayIdentifier(identifier, true) : day
}

const toApiRequest = (
  values: OrderIntakeLeadTimeFormValues,
  overrides: OrderIntakeLeadTimeOverrides
): PutOrderIntakeLeadTimeRequest => ({
  farDistanceThreshold: {
    isOverridden: overrides.farDistanceThreshold,
    value: {
      threshold: values.farDistanceThreshold,
      uomId: values.farDistanceThresholdUomId,
    },
  },
  leadTimes: {
    isOverridden: overrides.leadTimes,
    value: {
      standard: values.standard
        ? {
          applicableDays: values.standard.applicableDays.map(toApiDayCode),
          baseLeadTime: values.standard.baseLeadTime,
          baseLeadTimeUomId: values.standard.baseLeadTimeUomId,
          referenceEvent: values.standard.referenceEvent,
          farDistanceModifier: values.standard.farDistanceModifier,
          farDistanceModifierUomId: values.standard.farDistanceModifierUomId,
          calculationMethod: values.standard.calculationMethod,
        }
        : null!, // Unreachable — guarded by useOrderIntakeLeadTimeEntity.handleSave
      nonStandard: values.nonStandard.map((ns) => ({
        day: toApiDayCode(ns.day),
        baseLeadTime: ns.baseLeadTime,
        baseLeadTimeUomId: ns.baseLeadTimeUomId,
        referenceEvent: ns.referenceEvent,
        farDistanceModifier: ns.farDistanceModifier,
        farDistanceModifierUomId: ns.farDistanceModifierUomId,
        calculationMethod: ns.calculationMethod,
      })),
    },
  },
})

export const useCreateOrderIntakeLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: OrderIntakeLeadTimeFormValues; overrides: OrderIntakeLeadTimeOverrides }) => {
      const res = await api.post<OrderIntakeLeadTimeApiResponse>(
        `/tuc-configs/order-intake-lead-times/${scopeId}`,
        toApiRequest(values, overrides)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderIntakeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderIntakeLeadTime', countryId, businessLine] })
    },
  })
}

export const useCreateOrderIntakeLeadTimeWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values, overrides }: { scope: PendingScopeRequest; values: OrderIntakeLeadTimeFormValues; overrides: OrderIntakeLeadTimeOverrides }) => {
      const res = await api.post<OrderIntakeLeadTimeApiResponse>(
        '/tuc-configs/order-intake-lead-times',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values, overrides))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderIntakeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderIntakeLeadTime', countryId, businessLine] })
    },
  })
}

export const useUpdateOrderIntakeLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: OrderIntakeLeadTimeFormValues; overrides: OrderIntakeLeadTimeOverrides }) => {
      const res = await api.put<OrderIntakeLeadTimeApiResponse>(
        `/tuc-configs/order-intake-lead-times/${scopeId}`,
        toApiRequest(values, overrides)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderIntakeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderIntakeLeadTime', countryId, businessLine] })
    },
  })
}

export const useDeleteOrderIntakeLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/order-intake-lead-times/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderIntakeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderIntakeLeadTime', countryId, businessLine] })
    },
  })
}
